import React, { useEffect, useState } from 'react';
import { 
  Container, Tabs, Tab, Box, Typography, Button, Table, TableBody, 
  TableCell, TableHead, TableRow, Paper, TextField, MenuItem, Chip,
  Modal, Grid, IconButton
} from '@mui/material';
import useStore from '../store/useStore';
import OrderRow from '../components/OrderRow';
// Iconos
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import TagInput from '../components/TagInput';

const modalStyle = {
  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 600 }, maxHeight: '90vh', overflowY: 'auto', 
  bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24, p: 4,
};

const contactCustomer = (phone, orderId) => {
  const cleanPhone = phone.replace(/\D/g, ''); 
  const message = `Hola! Te contacto de Stiletto SHOP sobre tu pedido #${orderId.slice(-6).toUpperCase()}`;
  window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
};

export default function AdminDashboard() {
  const { 
    products, orders, fetchProducts, fetchOrders, 
    updateProduct, updateOrderStatus, addProduct 
  } = useStore();
  
  const [tab, setTab] = useState(0);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const initialFormState = { 
    nombre: '', precio: '', stock: '', categoria: '', descripcion: '',
    best_for: [], avoid_if: [], main_benefit: '', confidence_level: 'medium',
    price_quality_score: 0, ideal_queries: [],
    dermatologically_tested: false, cruelty_free: false, refund_days: 0
  };

  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchOrders();
  }, [fetchProducts, fetchOrders]);

  const handleEditClick = (product) => {
    setEditingId(product._id);
    
    setForm({
      // 1. Datos base del producto
      _id: product._id,
      nombre: product.nombre || "",
      brand: product.brand || "",
      descripcion: product.descripcion || "",
      precio: product.precio || 0,
      categoria: product.categoria || "",
      stock: product.stock || 0,
      imagenUrl: product.imagenUrl || "",
      activo: product.activo ?? true,

      // 2. Aplanar datos de Generative Experience (gx)
      main_benefit: product.gx?.main_benefit || "",
      best_for: product.gx?.best_for || [],
      avoid_if: product.gx?.avoid_if || [],
      ideal_queries: product.gx?.ideal_queries || [],
      confidence_level: product.gx?.confidence_level || "medium",
      price_quality_score: product.gx?.price_quality_score || 0,

      // 3. Aplanar datos de Confianza (trust)
      // Convertimos a string para que los Select de MUI coincidan con los MenuItem
      dermatologically_tested: product.trust?.dermatologically_tested ?? false,
      cruelty_free: product.trust?.cruelty_free ?? false,
      refund_days: product.trust?.refund_days || 0
    });

    setOpenEdit(true);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    
    // 1. Construir el objeto siguiendo el Schema de Mongoose
    const formattedData = {
      nombre: form.nombre,
      brand: form.brand,
      precio: form.precio,
      stock: form.stock,
      categoria: form.categoria,
      descripcion: form.descripcion,
      gx: {
        best_for: form.best_for,
        avoid_if: form.avoid_if,
        main_benefit: form.main_benefit,
        confidence_level: form.confidence_level,
        price_quality_score: form.price_quality_score,
        ideal_queries: form.ideal_queries,
      },
      trust: {
        dermatologically_tested: form.dermatologically_tested,
        cruelty_free: form.cruelty_free,
        refund_days: form.refund_days,
      }
    };

    try {
      if (selectedFile) {
        const formData = new FormData();
        formData.append('image', selectedFile);
        formData.append('nombre', formattedData.nombre);
        formData.append('brand', formattedData.brand);
        formData.append('precio', formattedData.precio);
        formData.append('stock', formattedData.stock);
        formData.append('categoria', formattedData.categoria);
        formData.append('descripcion', formattedData.descripcion);
        formData.append('gx', JSON.stringify(formattedData.gx));
        formData.append('trust', JSON.stringify(formattedData.trust));

        await updateProduct(editingId, formData);
      } else {
        await updateProduct(editingId, formattedData);
      }
      alert("Producto actualizado");
      setOpenEdit(false);
    } catch (error) {
      alert("Error al actualizar");
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!selectedFile) return alert("Selecciona una imagen");

    const formData = new FormData();
    formData.append('image', selectedFile);
    
    // Campos base
    formData.append('nombre', form.nombre);
    formData.append('brand', form.brand);
    formData.append('precio', form.precio);
    formData.append('stock', form.stock);
    formData.append('categoria', form.categoria);
    formData.append('descripcion', form.descripcion);

    // Objetos anidados (GXO y Trust)
    formData.append('gx', JSON.stringify({
      best_for: form.best_for,
      avoid_if: form.avoid_if,
      main_benefit: form.main_benefit,
      confidence_level: form.confidence_level,
      price_quality_score: form.price_quality_score,
      ideal_queries: form.ideal_queries,
    }));

    formData.append('trust', JSON.stringify({
      dermatologically_tested: form.dermatologically_tested,
      cruelty_free: form.cruelty_free,
      refund_days: form.refund_days,
    }));

    try {
      await addProduct(formData);
      alert("Producto creado");
      setOpenCreate(false);
      setForm(initialFormState);
      setSelectedFile(null);
    } catch (error) {
      alert("Error al crear");
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Dashboard ADMIN</Typography>
        <Button 
          variant="contained" startIcon={<AddIcon />} 
          onClick={() => { setForm(initialFormState); setOpenCreate(true); }}
          sx={{ bgcolor: '#0f172a' }}
        >
          Nuevo Producto
        </Button>
      </Box>
      
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Inventario" />
        <Tab label="Pedidos" />
      </Tabs>

      {tab === 0 && (
        <Paper elevation={0} sx={{ border: '1px solid #f0f0f0' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f9fafb' }}>
              <TableRow>
                <TableCell>Imagen</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Precio</TableCell>
                <TableCell>Stock</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p._id} hover>
                  <TableCell><img src={p.signedImageUrl} width="45" style={{borderRadius: 4}} alt=""/></TableCell>
                  <TableCell>{p.nombre}</TableCell>
                  <TableCell>{p.brand}</TableCell>
                  <TableCell>${p.precio}</TableCell>
                  <TableCell>{p.stock}</TableCell>
                  <TableCell>
                    <Chip 
                      label={p.activo ? "Activo" : "Pausado"} 
                      color={p.activo ? "success" : "default"}
                      onClick={() => updateProduct(p._id, { activo: !p.activo })}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleEditClick(p)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 1 && (
        <Paper elevation={0} sx={{ border: '1px solid #f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f9fafb' }}>
              <TableRow>
                <TableCell />
                <TableCell sx={{ fontWeight: 'bold' }}>Pedido</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cliente / Ubicación</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((o) => (
                <OrderRow key={o._id} order={o} updateOrderStatus={updateOrderStatus} contactCustomer={contactCustomer} />
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Modal open={openCreate || openEdit} onClose={() => {setOpenCreate(false); setOpenEdit(false);}}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2} fontWeight="bold">
            {openEdit ? 'Editar Producto' : 'Nuevo Producto'}
          </Typography>
          <form onSubmit={openEdit ? handleUpdateProduct : handleCreateProduct}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Nombre" value={form.nombre} onChange={(e)=>setForm({...form, nombre: e.target.value})} required/>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Marca" value={form.brand} onChange={(e)=>setForm({...form, brand: e.target.value})} required/>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Precio" type="number" value={form.precio} onChange={(e)=>setForm({...form, precio: e.target.value})} required/>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Stock" type="number" value={form.stock} onChange={(e)=>setForm({...form, stock: e.target.value})} required/>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Categoría" value={form.categoria} onChange={(e)=>setForm({...form, categoria: e.target.value})} required/>
              </Grid>
              
              {/* SECCIÓN GXO - AGENT FIRST */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Datos para Agentes (GXO)</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Beneficio Principal" value={form.main_benefit} onChange={(e)=>setForm({...form, main_benefit: e.target.value})} />
              </Grid>
              
              <Grid item xs={12}>
                <TagInput 
                  label="Mejor Para" 
                  value={form.best_for} 
                  onChange={(tags) => setForm({...form, best_for: tags})} 
                  placeholder="Ej: piel seca, anti-edad..."
                />
              </Grid>

              <Grid item xs={12}>
                <TagInput 
                  label="Evitar Si" 
                  value={form.avoid_if} 
                  onChange={(tags) => setForm({...form, avoid_if: tags})} 
                  placeholder="Ej: piel grasa, alergia a Vitamina C..."
                />
              </Grid>

              <Grid item xs={12}>
                <TagInput 
                  label="Consultas Ideales (Keywords)" 
                  value={form.ideal_queries} 
                  onChange={(tags) => setForm({...form, ideal_queries: tags})} 
                />
              </Grid>

              <Grid item xs={6}>
                <TextField fullWidth label="Nivel de Confianza" select value={form.confidence_level} onChange={(e)=>setForm({...form, confidence_level: e.target.value})}>
                  <MenuItem value="low">Bajo</MenuItem>
                  <MenuItem value="medium">Medio</MenuItem>
                  <MenuItem value="high">Alto</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Puntaje Calidad-Precio (0-1)" type="number" inputProps={{ step: "0.1", min: "0", max: "1" }} value={form.price_quality_score} onChange={(e)=>setForm({...form, price_quality_score: e.target.value})} />
              </Grid>

              {/* SECCIÓN TRUST */}
              <Grid item xs={4}>
                <TextField fullWidth label="Derm. Testeado" select value={form.dermatologically_tested} onChange={(e)=>setForm({...form, dermatologically_tested: e.target.value === 'true'})}>
                  <MenuItem value="true">Sí</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth label="Cruelty Free" select value={form.cruelty_free} onChange={(e)=>setForm({...form, cruelty_free: e.target.value === 'true'})}>
                  <MenuItem value="true">Sí</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth label="Días Reembolso" type="number" value={form.refund_days} onChange={(e)=>setForm({...form, refund_days: e.target.value})} />
              </Grid>

              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth startIcon={<CloudUploadIcon />}>
                  {selectedFile ? selectedFile.name : "Subir/Cambiar Imagen"}
                  <input type="file" hidden onChange={(e) => setSelectedFile(e.target.files[0])} />
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button fullWidth variant="contained" type="submit" sx={{ bgcolor: '#F7D060', color: '#000', py: 1.5, fontWeight: 'bold' }}>
                  {openEdit ? 'Actualizar Producto' : 'Guardar Producto'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Modal>
    </Container>
  );
}